<template>
    <div v-if="shouldShowBanner" class="handball-banner-container">
        <div class="handball-banner-content">
            <img :src="activeBanner.image" class="banner-image" :alt="activeBanner.alt" @click="openHandballNet" />
            <v-icon :size="isMobile ? 'small' : 'x-large'" icon="mdi-close" class="close-button position-absolute"
                @click="closeBanner" aria-label="Schließen"></v-icon>
        </div>
    </div>
</template>

<script>
// Import der Banner-Bilder
import registerBannerSrc from '@/assets/images/Registrieren_Sticky-Ad.gif';
import dhbBannerSrc from '@/assets/images/dhb_ihf2027_banner_600x100.jpg';

export default {
    name: 'HandballNetBanner',

    data() {
        return {
            shouldShowBanner: false,
            currentBannerId: '1.4', // Die aktuelle Banner-ID direkt in der Komponente
            lastActiveBannerId: null, // Speichert die ID des zuletzt aktiven Banners

            // Banner-Konfiguration mit Zeiträumen
            banners: [
                {
                    id: 'dhb-ihf2027',
                    image: dhbBannerSrc,
                    alt: 'IHF 2027 Handball WM Deutschland',
                    url: 'https://www.eventim.de/campaign/ihf2027-m',
                    // Zeitraum: 15. Dezember 2025, 12:00 Uhr bis 31. Dezember 2026, 23:59:59 Uhr
                    startDate: new Date('2025-12-15T12:00:00'),
                    endDate: new Date('2026-12-31T23:59:59')
                },
                {
                    id: 'register',
                    image: registerBannerSrc,
                    alt: 'Handball.net - Folge deinen Teams & Ligen',
                    url: 'https://www.handball.net/?utm_source=handballstatistikende&utm_medium=banner&utm_campaign=registrieren',
                    // Standard-Banner (wird gezeigt, wenn kein anderer Banner aktiv ist)
                    startDate: null,
                    endDate: null
                }
            ],

            // Einstellungen für Banner-Anzeige
            bannerSettings: {
                lastShown: null,
                dismissCount: 0,
                wasClicked: false,
                interval: 3, // Standard: Alle 3 Tage
                bannerId: null,
                activelyDismissed: false // Flag für aktives Wegklicken
            }
        };
    },

    mounted() {
        this.loadBannerSettings();

        // Überprüfe, ob die Banner-ID sich geändert hat
        if (this.bannerSettings.bannerId !== this.currentBannerId) {
            this.resetBannerScheduling();
        } else {
            this.checkBannerSchedule();
        }

        // Initialen Banner-Typ speichern
        this.lastActiveBannerId = this.activeBanner.id;

        // Event-Listener für das Verlassen der Seite hinzufügen
        window.addEventListener('beforeunload', this.handlePageLeave);
    },

    beforeUnmount() { // Korrektur des Methodennamens von berforeUnmount zu beforeUnmount
        // Event-Listener entfernen
        window.removeEventListener('beforeunload', this.handlePageLeave);
    },

    watch: {
        // Überwacht den aktiven Banner und setzt die Einstellungen zurück, wenn sich der Banner-Typ ändert
        activeBanner: {
            handler(newBanner) {
                if (this.lastActiveBannerId && newBanner.id !== this.lastActiveBannerId) {
                    console.log(`Banner gewechselt von ${this.lastActiveBannerId} zu ${newBanner.id}`);
                    this.resetBannerScheduling();
                }
                // Aktuellen Banner-Typ speichern
                this.lastActiveBannerId = newBanner.id;
            },
            immediate: true
        }
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },

        // Bestimmt den aktuell aktiven Banner basierend auf dem Datum
        activeBanner() {
            const now = new Date();

            // Suche nach einem Banner, der aktuell im definierten Zeitraum ist
            const timedBanner = this.banners.find(banner => {
                if (banner.startDate && banner.endDate) {
                    return now >= banner.startDate && now <= banner.endDate;
                }
                return false;
            });

            // Wenn ein zeitbasierter Banner gefunden wurde, gib diesen zurück
            if (timedBanner) {
                return timedBanner;
            }

            // Ansonsten gib den Standard-Banner zurück (ohne Zeitraum)
            return this.banners.find(banner => !banner.startDate && !banner.endDate);
        }
    },

    methods: {
        loadBannerSettings() {
            const storedSettings = localStorage.getItem('handballBannerSettings');
            if (storedSettings) {
                this.bannerSettings = JSON.parse(storedSettings);
            } else {
                // Initialisiere mit Standard-Einstellungen
                this.bannerSettings = {
                    lastShown: null,
                    dismissCount: 0,
                    wasClicked: false,
                    interval: 3, // Standard: Alle 3 Tage
                    bannerId: this.currentBannerId,
                    activelyDismissed: false
                };
                this.saveBannerSettings();
            }
        },

        saveBannerSettings() {
            localStorage.setItem('handballBannerSettings', JSON.stringify(this.bannerSettings));
        },

        checkBannerSchedule() {
            const now = new Date().getTime();
            const lastShown = this.bannerSettings.lastShown;

            // Wenn noch nie angezeigt, immer anzeigen
            if (!lastShown) {
                this.shouldShowBanner = true;
                this.bannerSettings.lastShown = now;
                this.bannerSettings.activelyDismissed = false;
                this.saveBannerSettings();
                return;
            }

            // Prüfe, ob der letzte Besuch am selben Tag war
            const lastDate = new Date(lastShown);
            const today = new Date();
            const isSameDay = lastDate.getDate() === today.getDate() &&
                lastDate.getMonth() === today.getMonth() &&
                lastDate.getFullYear() === today.getFullYear();

            // Wenn der Banner beim letzten Mal nicht aktiv weggeklickt wurde und es ist derselbe Tag,
            // zeige ihn erneut an
            if (!this.bannerSettings.activelyDismissed && isSameDay) {
                this.shouldShowBanner = true;
                this.bannerSettings.lastShown = now; // Aktualisiere den Zeitstempel
                this.saveBannerSettings();
                return;
            }

            // Wenn aktiv weggeklickt oder ein anderer Tag, prüfe das normale Intervall
            if (now - lastShown >= this.bannerSettings.interval * 24 * 60 * 60 * 1000) {
                this.shouldShowBanner = true;
                this.bannerSettings.lastShown = now;
                this.bannerSettings.activelyDismissed = false; // Zurücksetzen für neue Anzeige
                this.saveBannerSettings();
            } else {
                this.shouldShowBanner = false;
            }
        },

        closeBanner() {
            this.shouldShowBanner = false;

            // Markieren, dass der Banner aktiv weggeklickt wurde
            this.bannerSettings.activelyDismissed = true;
            this.bannerSettings.dismissCount += 1;

            // Passt das Intervall basierend auf dem Nutzerverhalten an
            if (this.bannerSettings.dismissCount >= 3 && !this.bannerSettings.wasClicked) {
                // Nach 3 Mal weggeklickt ohne anzuklicken: Alle 5 Tage anzeigen
                this.bannerSettings.interval = 5;
            }

            this.saveBannerSettings();
        },

        openHandballNet() {
            // Öffnet die URL des aktuell aktiven Banners
            window.open(this.activeBanner.url, '_blank');

            // Tracking-Request
            fetch(`${this.$router.options.backendPath}/log_click.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: `type=${encodeURIComponent('Banner')}&url=${encodeURIComponent(this.activeBanner.url)}`
            });

            // Aktualisiert die Banner-Einstellungen nach dem Klick
            this.bannerSettings.wasClicked = true;
            this.bannerSettings.activelyDismissed = true; // Als aktiv interagiert markieren
            this.bannerSettings.interval = 7; // Bei Klick: Nur noch einmal pro Woche anzeigen
            this.shouldShowBanner = false;
            this.saveBannerSettings();
        },

        resetBannerScheduling() {
            this.bannerSettings = {
                lastShown: null,
                dismissCount: 0,
                wasClicked: false,
                interval: 3, // Zurück zum Standard: Alle 3 Tage
                bannerId: this.currentBannerId, // Aktuelle Banner-ID speichern
                activelyDismissed: false // Zurücksetzen des aktiv-weggeklickt Flags
            };
            this.saveBannerSettings();
            this.checkBannerSchedule(); // Sofort prüfen, ob Banner angezeigt werden soll
        },

        // Methode, die beim Window 'beforeunload'-Event ausgeführt werden könnte
        handlePageLeave() {
            // Wenn Banner angezeigt wird und weder geklickt noch aktiv weggeklickt wurde,
            // dann behalte den Dismiss-Count gleich
            if (this.shouldShowBanner && !this.bannerSettings.activelyDismissed) {
                // Nichts machen, Banner gilt nicht als weggeklickt
            }
        }
    }
};
</script>

<style scoped>
@keyframes slideUp {
    from {
        transform: translateY(100%);
    }

    to {
        transform: translateY(0);
    }
}

.handball-banner-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    animation: slideUp 0.5s ease-out;
    pointer-events: none;
    /* Allow clicks to pass through except for clickable elements */
}

.handball-banner-content {
    position: relative;
    display: flex;
    justify-content: center;
    pointer-events: auto;
    /* Make this element clickable */
}

.banner-image {
    max-width: 100%;
    height: auto;
    display: block;
}

.close-button {
    top: 4px;
    right: 4px;
}

/* Add responsive max-width to center the banner */
@media (min-width: 960px) {
    .handball-banner-content {
        max-width: 800px;
    }
}

@media (min-width: 600px) and (max-width: 959px) {
    .handball-banner-content {
        max-width: 600px;
    }
}

@media (max-width: 599px) {
    .handball-banner-content {
        max-width: 100%;
    }
}
</style>